import React, { useState } from "react";
import Navbar from "../components/Navbar";
import AdminForm from "../components/AdminForm";
import { createUser } from "../services/firebase";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import ChallengeCalculator from "../components/weeklyChallenges/challengeCalculator";
import Blog from "../components/Blog/Blog";
import EditBlogModal from "../components/Blog/EditBlogModal";
import Shop from "../components/product/Product";
import Product from "../components/product/Product";
import SocialFeed from "../components/SocialFeed/SocialFeed";
import UserInspirations from "../components/userInspirations/UserInspirations";
import BlogAuthorsTab from "../components/BlogAuthors/BlogAuthorsTab";
import Configure from "../components/Configure/Configure";
import LoserOfTheWeek from "../components/LoserOfTheWeek";
import { User } from "lucide-react";

interface TabProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

const Tab: React.FC<TabProps> = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left px-4 py-2 font-semibold transition-colors duration-200 ${
      active
        ? "text-blue-600 bg-blue-100 border-l-4 border-blue-600"
        : "text-gray-500 hover:text-blue-500 hover:bg-gray-100"
    }`}
    style={{ outline: 'none' }}
  >
    {label}
  </button>
);

const AdminPage: React.FC = () => {
  const { theme } = useTheme();
  const { user, userRole } = useAuth();
  const [activeTab, setActiveTab] = useState("users");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState<
    "user" | "commissioner" | "admin"
  >("user");
  const [userCreationError, setUserCreationError] = useState<string | null>(
    null
  );
  const [userCreationSuccess, setUserCreationSuccess] = useState<string | null>(
    null
  );
  const isAdmin = userRole === "admin";

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setUserCreationError(null);
    setUserCreationSuccess(null);

    if (!user || userRole !== "admin") {
      setUserCreationError("Only admins can create new users");
      return;
    }

    try {
      const result = await createUser(
        newUserEmail,
        newUserPassword,
        newUserRole
      );
      if (result.success) {
        setUserCreationSuccess(
          `User created successfully with ID: ${result.uid}`
        );
        setNewUserEmail("");
        setNewUserPassword("");
        setNewUserRole("user");
      } else {
        setUserCreationError("Failed to create user");
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes("auth/email-already-in-use")) {
          setUserCreationError(
            "Email is already in use. Please use a different email."
          );
        } else if (error.message.includes("auth/weak-password")) {
          setUserCreationError(
            "Password is too weak. Please use a stronger password."
          );
        } else {
          setUserCreationError(`Error creating user: ${error.message}`);
        }
      } else {
        setUserCreationError("An unknown error occurred while creating user");
      }
    }
  };

  if (userRole !== "admin") {
    return <div>Access Denied. Only admins can view this page.</div>;
  }

  const renderUserCreationForm = () => (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h2 className="text-2xl font-bold mb-4">Create New User</h2>
        <form
          onSubmit={handleCreateUser}
          className={`${theme === "dark" ? "bg-gray-800 text-white" : "bg-white text-gray-800"
            } shadow-md rounded px-8 pt-6 pb-8 mb-8`}
        >
          {userCreationError && (
            <div className="mb-4 text-red-500">{userCreationError}</div>
          )}
          {userCreationSuccess && (
            <div className="mb-4 text-green-500">{userCreationSuccess}</div>
          )}
          <div className="mb-4">
            <label className="block text-sm font-bold mb-2" htmlFor="newUserEmail">
              Email
            </label>
            <input
              type="email"
              id="newUserEmail"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              className={`shadow appearance-none border rounded w-full py-2 px-3 ${theme === "dark" ? "bg-gray-700 text-white" : "text-gray-700"
                } leading-tight focus:outline-none focus:shadow-outline`}
              required
            />
          </div>
          <div className="mb-4">
            <label
              className="block text-sm font-bold mb-2"
              htmlFor="newUserPassword"
            >
              Password
            </label>
            <input
              type="password"
              id="newUserPassword"
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              className={`shadow appearance-none border rounded w-full py-2 px-3 ${theme === "dark" ? "bg-gray-700 text-white" : "text-gray-700"
                } leading-tight focus:outline-none focus:shadow-outline`}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-bold mb-2" htmlFor="newUserRole">
              Role
            </label>
            <select
              id="newUserRole"
              value={newUserRole}
              onChange={(e) =>
                setNewUserRole(e.target.value as "user" | "commissioner" | "admin")
              }
              className={`shadow appearance-none border rounded w-full py-2 px-3 ${theme === "dark" ? "bg-gray-700 text-white" : "text-gray-700"
                } leading-tight focus:outline-none focus:shadow-outline`}
            >
              <option value="user">User</option>
              <option value="commissioner">Commissioner</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="flex items-center justify-end">
            <button
              type="submit"
              className="px-6 py-3 bg-[#18f7b1] text-black rounded-lg hover:bg-[#0fd494] 
            flex items-center gap-2 transition-all duration-200"
            >
              <User size={20} />
              Create User
            </button>
          </div>
        </form>
      </div>
    </div>
    
  );

  return (
    <div className="min-h-screen bg-gray-100 mt-[50px]">
      <Navbar />
      <main className="container mx-auto px-4 py-8 pt-[50px]">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Admin Dashboard</h1>
        <div style={{ display: 'flex', minHeight: '60vh', background: 'transparent' }}>
          {/* Sidebar Tabs - now much narrower */}
          <div
            style={{
              width: 250,
              borderRight: '1px solid #e5e7eb',
              background: '#fff',
              borderRadius: '8px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              flexShrink: 0,
              maxHeight: '80vh',
              overflowY: 'auto',
              position: 'sticky',
              top: 100,
              zIndex: 10
            }}
            className="py-4 mr-4"
          >
            <Tab label="User Management" active={activeTab === "users"} onClick={() => setActiveTab("users")} />
            <Tab label="Content Management" active={activeTab === "content"} onClick={() => setActiveTab("content")} />
            <Tab label="Challenges" active={activeTab === "calculator"} onClick={() => setActiveTab("calculator")} />
            <Tab label="Articles" active={activeTab === "blog"} onClick={() => setActiveTab("blog")} />
            <Tab label="LOTW" active={activeTab === "lotw"} onClick={() => setActiveTab("lotw")} />
            <Tab label="Product" active={activeTab === "product"} onClick={() => setActiveTab("product")} />
            <Tab label="Social Feed" active={activeTab === "socialfeed"} onClick={() => setActiveTab("socialfeed")} />
            <Tab label="User Inspirations" active={activeTab === "userInspirations"} onClick={() => setActiveTab("userInspirations")} />
            <Tab label="Authors" active={activeTab === "blogAuthorsTab"} onClick={() => setActiveTab("blogAuthorsTab")} />
            <Tab label="Default Images" active={activeTab === "configure"} onClick={() => setActiveTab("configure")} />
          </div>
          {/* Content Area - now much wider */}
          <div style={{ flex: 3, minWidth: 0 }}>
            <div>
              {activeTab === "users" && (
                  <div className="bg-white rounded-lg shadow-md p-6">
                   {renderUserCreationForm()}
                  </div>
                )}
              {activeTab === "content" && (
                <div className="bg-white rounded-lg shadow-md p-6">
                <AdminForm />
                </div>
              )}
              {activeTab === "calculator" && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <ChallengeCalculator onSaveSuccess={() => console.log("Saved!")} />
                </div>
              )}
              {activeTab === "blog" && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <Blog onSaveSuccess={() => { console.log("Saved!"); }} />
                </div>
              )}
              {activeTab === "product" && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <Product onSaveSuccess={() => console.log("Saved!")} />
                </div>
              )}
              {activeTab === "socialfeed" && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <SocialFeed onSaveSuccess={() => console.log("Saved!")} />
                </div>
              )}
              {activeTab === "userInspirations" && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <UserInspirations />
                </div>
              )}
              {activeTab === "blogAuthorsTab" && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <BlogAuthorsTab />
                </div>
              )}
              {activeTab === "configure" && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <Configure />
                </div>
              )}
              {activeTab === "lotw" && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <LoserOfTheWeek />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
